import { directApiInstance } from './httpClient';

/**
 * SAM-553 — booking console ops (cancel-on-behalf, cancel-request queue, void/refund, no-show
 * capture, receipt, QR reissue/revoke, changelog).
 *
 * Every call here goes through `directApiInstance` (no `/crud` prefix) because these are the
 * booking lambda's own routes, not CRUD-table endpoints — same reasoning as `useCancelBooking` in
 * `useBookings.ts`.
 *
 * The backend contract (sam-lambda-functions PR "feat/SAM-553-booking-ops-api") ships in parallel
 * with this UI. A route that isn't deployed to dev yet answers 404 (unmapped API Gateway route) or
 * 501 (deployed but intentionally unimplemented) — both are surfaced here as
 * `BookingOpsUnavailableError` so callers can render "not available yet" instead of crashing on
 * what would otherwise look like a real failure.
 *
 * Response envelope: every route responds `{ success, data }` (matching the rest of this app's
 * direct-API endpoints, e.g. `/bookings/{id}` DELETE). `directApiInstance`'s response interceptor
 * already unwraps the axios response to that JSON body — see httpClient.ts — so callers here only
 * ever peel off `.data`.
 */

export class BookingOpsUnavailableError extends Error {
  readonly path: string;
  readonly status?: number;

  constructor(path: string, status?: number) {
    super(`Booking ops route not available yet: ${path}`);
    this.name = 'BookingOpsUnavailableError';
    this.path = path;
    this.status = status;
  }
}

function isUnavailableStatus(status?: number): boolean {
  return status === 404 || status === 501;
}

async function request<T>(config: Parameters<typeof directApiInstance>[0]): Promise<T> {
  try {
    // See the module docstring: the resolved value is already the unwrapped `{success, data}`
    // body, despite the AxiosResponse<T> type directApiInstance carries over from axios.request.
    const body = (await directApiInstance(config)) as unknown as T;
    return body;
  } catch (err: any) {
    const status = err?.response?.status;
    if (isUnavailableStatus(status)) {
      throw new BookingOpsUnavailableError(String(config.url), status);
    }
    throw err;
  }
}

export interface ApiEnvelope<T> {
  success: boolean;
  data: T;
}

// ---------------------------------------------------------------------------
// Changelog — GET /bookings/{id}/history
// ---------------------------------------------------------------------------

export interface BookingHistoryEntry {
  id: string;
  action: string;
  actor_user_id?: string | number | null;
  actor_kind?: string | null;
  reason?: string | null;
  before?: Record<string, any> | null;
  after?: Record<string, any> | null;
  created_at: string;
}

export async function getBookingHistory(bookingId: string): Promise<BookingHistoryEntry[]> {
  const res = await request<ApiEnvelope<BookingHistoryEntry[]>>({
    url: `/bookings/${bookingId}/history`,
    method: 'GET',
  });
  return res.data ?? [];
}

// ---------------------------------------------------------------------------
// Cancellation — policy preview, cancel-on-behalf, and the pending-request queue
// ---------------------------------------------------------------------------

export interface BookingCancellationPolicy {
  /** Guest-facing copy explaining the applicable policy — show this before confirming. */
  describe_for_guest?: string | null;
  threshold_minutes?: number | null;
  minutes_before?: number | null;
  within_policy?: boolean;
  penalty_applies?: boolean;
}

/**
 * A fresh single-booking read, used only to pull `cancellation.describe_for_guest` right before an
 * operator confirms a cancel — the list/detail data already on screen (useBookings.ts's `Booking`)
 * predates this field and is not refetched on every render, so this is a deliberate extra call
 * rather than a read of stale local state.
 */
export async function getBookingCancellationPolicy(
  bookingId: string,
): Promise<BookingCancellationPolicy | null> {
  const res = await request<ApiEnvelope<{ cancellation?: BookingCancellationPolicy | null }>>({
    url: `/bookings/${bookingId}`,
    method: 'GET',
  });
  return res.data?.cancellation ?? null;
}

export async function cancelBookingOnBehalf(bookingId: string, reason: string): Promise<void> {
  await request<ApiEnvelope<unknown>>({
    url: `/bookings/${bookingId}`,
    method: 'DELETE',
    data: { reason },
  });
}

export interface CancelRequest {
  id: string;
  booking_id: string;
  business_id?: string | null;
  status?: string | null;
  reason?: string | null;
  requested_by?: string | null;
  created_at: string;
  // Joined guest/booking summary fields, when the backend includes them — optional because the
  // exact shape of this join isn't finalized yet on the parallel backend PR.
  user_name?: string | null;
  user_email?: string | null;
  service_name?: string | null;
  reservation_start?: string | null;
  confirmation_code?: string | null;
}

export async function getCancelRequests(businessId: string): Promise<CancelRequest[]> {
  const res = await request<ApiEnvelope<CancelRequest[]>>({
    url: '/bookings/cancel-requests',
    method: 'GET',
    params: { business_id: businessId },
  });
  return res.data ?? [];
}

export async function approveCancelRequest(bookingId: string, reason: string): Promise<void> {
  await request<ApiEnvelope<unknown>>({
    url: `/bookings/${bookingId}/cancel-requests/approve`,
    method: 'POST',
    data: { reason },
  });
}

export async function rejectCancelRequest(bookingId: string, reason: string): Promise<void> {
  await request<ApiEnvelope<unknown>>({
    url: `/bookings/${bookingId}/cancel-requests/reject`,
    method: 'POST',
    data: { reason },
  });
}

// ---------------------------------------------------------------------------
// Void / refund — irreversible, gated on VOID_PAYMENT / REFUND_PAYMENT (utils/capabilities.ts)
// ---------------------------------------------------------------------------

export async function voidBooking(bookingId: string, reason: string): Promise<void> {
  await request<ApiEnvelope<unknown>>({
    url: `/bookings/${bookingId}/void`,
    method: 'POST',
    data: { reason },
  });
}

export interface RefundResult {
  amount?: number;
  currency?: string;
}

/**
 * `amount` is the operator-typed figure, or omitted for a full refund of the captured amount —
 * never computed or sent as a guess from anything already on screen. See CLAUDE.md: "Never send a
 * price from the client."
 */
export async function refundBooking(
  bookingId: string,
  reason: string,
  amount?: number,
): Promise<RefundResult> {
  const res = await request<ApiEnvelope<RefundResult>>({
    url: `/bookings/${bookingId}/refund`,
    method: 'POST',
    data: amount != null ? { amount, reason } : { reason },
  });
  return res.data ?? {};
}

// ---------------------------------------------------------------------------
// No-show
// ---------------------------------------------------------------------------

export interface NoShowResult {
  captured: boolean;
}

export async function markNoShow(bookingId: string, reason?: string): Promise<NoShowResult> {
  const res = await request<ApiEnvelope<NoShowResult>>({
    url: `/bookings/${bookingId}/no-show`,
    method: 'POST',
    data: reason ? { reason } : {},
  });
  return res.data ?? { captured: false };
}

// ---------------------------------------------------------------------------
// Receipt — GET /bookings/{id}/receipt (PR #413), resend
// ---------------------------------------------------------------------------

export interface ReceiptLineItem {
  // SAM-590 — "add_on" identifies an add-on line, sent with a bilingual `label` object instead of
  // the flat string every other line uses today; other line types keep the flat string.
  type?: 'add_on' | string | null;
  label?: string | { en?: string | null; es?: string | null } | null;
  quantity?: number | null;
  unit_price?: number | null;
  total?: number | null;
  // SAM-590 — add_on lines carry `subtotal` instead of `total` (PR #419's receipt shape).
  subtotal?: number | null;
  // SAM-624 — pricing v2. "unit" is the cabana/unit base charge (SAM-591). The receipt builder
  // copies only id/label/quantity/unit_price/subtotal/type today, so these are read when the
  // backend starts passing the stored line's own fields through.
  rate_group?: string | null;
  event_size?: string | null;
  resource_key?: string | null;
  resource_name?: string | null;
  included_pax?: number | null;
  max_pax?: number | null;
  season?: string | { id?: string; label?: string | { en?: string | null; es?: string | null } | null } | null;
  season_label?: string | { en?: string | null; es?: string | null } | null;
}

export interface ReceiptMovement {
  type: string; // e.g. charge / refund / void
  amount: number;
  currency?: string | null;
  reference?: string | null;
  created_at?: string | null;
}

export interface BookingReceipt {
  booking_id: string;
  line_items: ReceiptLineItem[];
  total: number;
  currency?: string | null;
  movements: ReceiptMovement[];
}

export async function getBookingReceipt(bookingId: string): Promise<BookingReceipt> {
  const res = await request<ApiEnvelope<BookingReceipt>>({
    url: `/bookings/${bookingId}/receipt`,
    method: 'GET',
  });
  return res.data;
}

export async function resendBookingReceipt(bookingId: string): Promise<void> {
  await request<ApiEnvelope<unknown>>({
    url: `/bookings/${bookingId}/receipt/send`,
    method: 'POST',
  });
}

// ---------------------------------------------------------------------------
// QR / access
// ---------------------------------------------------------------------------

export async function reissueBookingAccess(bookingId: string): Promise<void> {
  await request<ApiEnvelope<unknown>>({
    url: `/bookings/${bookingId}/access/reissue`,
    method: 'POST',
  });
}

export async function revokeBookingAccess(bookingId: string): Promise<void> {
  await request<ApiEnvelope<unknown>>({
    url: `/bookings/${bookingId}/access/revoke`,
    method: 'POST',
  });
}
